"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { auth } from "./context/firebase";
import {
  Menu,
  X,
  ChevronDown,
  MessageSquare,
  Video,
  Monitor,
  ShieldCheck,
  Info,
  Sparkles,
  ScanSearch,
  Globe,
  PhoneCall,
  Film,
  FileText,
} from "lucide-react";
import { LayoutWrapper } from "./layout-wrapper";
import { isSelfServe, useVisitorRegion } from "@/lib/visitorRegion";

// --- Data Structures ---
// B2B-only navigation. Candidate practice surfaces stay live for SEO but are
// linked from the footer's "For candidates" entry only, never from the header.
const platformLinks = [
  {
    name: "Platform Overview",
    description: "The full first round, from applications to shortlist.",
    href: "/recruiters",
    icon: <Info size={18} className="text-gray-500" />,
  },
  {
    name: "AI Interviewer",
    description: "Runs your first round with real voice interviews.",
    href: "/recruiters/ai-interviewer",
    icon: <ShieldCheck size={18} className="text-gray-500" />,
  },
  {
    name: "Phone Screening",
    description: "An AI agent that answers and makes screening calls.",
    href: "/recruiters/ai-phone-screening",
    icon: <PhoneCall size={18} className="text-gray-500" />,
  },
  {
    name: "AI Video Interview Software",
    description: "Two-way video interviews, scored and clipped.",
    href: "/recruiters/ai-video-interview-software",
    icon: <Video size={18} className="text-gray-500" />,
  },
  {
    name: "Interview Highlight Clips",
    description: "Clips of the best answers, plus a shortlist reel.",
    href: "/recruiters/interview-highlight-clips",
    icon: <Film size={18} className="text-gray-500" />,
  },
  {
    name: "Call to Submittal",
    description: "Your screening call, written up into a client submittal.",
    href: "/recruiters/call-to-submittal",
    icon: <FileText size={18} className="text-gray-500" />,
  },
  {
    name: "AI Resume Screening",
    description: "Score every CV against the JD",
    href: "/recruiters/ai-resume-screening",
    icon: <ScanSearch size={18} className="text-gray-500" />,
  },
  {
    name: "Agentic Resume Search",
    description: "An AI agent that searches your talent pool",
    href: "/recruiters/agentic-resume-search",
    icon: <Sparkles size={18} className="text-gray-500" />,
  },
  {
    name: "Career Page Builder",
    description: "Branded careers site with built-in ATS",
    href: "/recruiters/career-page-builder",
    icon: <Globe size={18} className="text-gray-500" />,
  },
  {
    name: "Connect to your AI assistant",
    description: "Run hiring from ChatGPT, Claude, Gemini, and more.",
    href: "/connect",
    icon: <Sparkles size={18} className="text-purple-600" />,
  },
  {
    name: "AI Interview Bot",
    description: "Intelligent agent for first-round screening.",
    href: "/recruiters/ai-interview-bot",
    icon: <Monitor size={18} className="text-gray-500" />,
  },
  {
    name: "AI Interview Software",
    description: "What to look for, and how platforms compare.",
    href: "/recruiters/ai-interview-software",
    icon: <MessageSquare size={18} className="text-gray-500" />,
  },
];

const navLinks = [
  { name: "Platform", dropdown: "platform" },
  { name: "Blog", href: "/blog" },
  { name: "About", href: "/about" },
  { name: "Tools", href: "/tools" },
  { name: "Contact", href: "/contact" },
];

// Sales-led regions (and the moment before the region is known) book a demo;
// self-serve regions start free.
const DEMO_URL = "/book-a-demo";
const START_FREE_URL = "/auth/register?type=hr";

// --- Helper Component for Dropdown Items ---
const DropdownItem = ({ item, isActive, onClick }) => (
  <Link
    href={item.href}
    onClick={onClick}
    className={`group/item flex gap-4 p-3 rounded-xl transition-colors ${isActive ? "bg-blue-50" : "hover:bg-gray-50"
      }`}
  >
    <div
      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${isActive ? "bg-blue-100" : "bg-gray-50 group-hover/item:bg-blue-100"
        }`}
    >
      {item.icon}
    </div>

    <div>
      <div
        className={`font-bold text-sm transition-colors ${isActive ? "text-blue-600" : "text-gray-900 group-hover/item:text-blue-600"
          }`}
      >
        {item.name}
      </div>

      <div className="text-[12px] text-gray-500 leading-snug mt-0.5">
        {item.description}
      </div>
    </div>
  </Link>
);


const Navbar = () => {
  const pathname = usePathname();
  const selfServe = isSelfServe(useVisitorRegion());
  const demoHref = selfServe ? START_FREE_URL : DEMO_URL;
  const demoLabel = selfServe ? "Start free" : "Book a Demo";
  const demoAnalyticsId = selfServe ? "start_free" : undefined;
  const [isOpen, setIsOpen] = useState(false);
  // Track which mobile dropdown is open by name
  const [mobileMenuOpen, setMobileMenuOpen] = useState(null);
  const disableAuth = String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";
  const signInHref = disableAuth ? "/dashboard/agency" : "/auth/login?type=hr";

  // Auth-state-aware CTA: "Sign In" when logged out, "Go to Dashboard" when
  // logged in. Same pattern Google Cloud / Vercel / Stripe use on their
  // marketing nav — avoids the "do I sign in again?" moment that confuses
  // users who are already authenticated.
  const [isSignedIn, setIsSignedIn] = useState(false);
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setIsSignedIn(!!u));
    setIsSignedIn(!!auth.currentUser);
    return unsub;
  }, []);
  const ctaHref = isSignedIn ? "/dashboard" : signInHref;
  const ctaLabel = isSignedIn ? "Dashboard" : "Sign In";

  const isActive = (href) => pathname === href;

  return (
    <LayoutWrapper>
      <div
        className="w-full bg-white fixed top-0 left-0 right-0"
        style={{ zIndex: 50 }}
      >
        <nav className="flex items-center justify-between py-3 main-width max-w-[1280px] 2xl:max-w-[1400px] mx-auto relative">

          {/* Divider */}
          {pathname !== "/" && (
            <div className="absolute left-0 bottom-0 w-full h-[1px] bg-[#4B5162] opacity-20" />
          )}

          {/* Logo */}
          <Link href="/" className="z-50">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-gray-900 tracking-tight">
                AI Interview Agents
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center gap-8 text-[15px] font-medium">
            {navLinks.map((link) => (
              <li key={link.name} className="relative group">
                {!link.dropdown ? (
                  <Link
                    href={link.href}
                    className={`transition-colors py-2 border-b-2 ${isActive(link.href)
                      ? "text-blue-600 border-blue-600"
                      : "text-gray-600 border-transparent hover:text-gray-900"
                      }`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <>
                    <div className="flex items-center gap-1 cursor-pointer text-gray-600 hover:text-gray-900 transition-colors py-2 border-b-2 border-transparent group-hover:border-blue-600 group-hover:text-blue-600">
                      <span>{link.name}</span>
                      <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                    </div>

                    {/* MEGA DROPDOWN */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-[44px] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] rounded-2xl px-6 py-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 w-[700px]">
                      <div className="grid grid-cols-2 gap-x-12 gap-y-2">
                        {platformLinks.map((item) => (
                          <DropdownItem
                            key={item.name}
                            item={item}
                            isActive={pathname === item.href}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop Buttons + Mobile Toggle */}
          <div className="flex items-center gap-3 py-2">
            <a
              href={demoHref}
              data-analytics-id={demoAnalyticsId}
              className="hidden lg:inline-flex items-center justify-center border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all"
            >
              {demoLabel}
            </a>
            <Link
              href={ctaHref}
              className="hidden lg:inline-flex items-center justify-center bg-[#5850EC] hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-semibold text-sm transition-all"
            >
              {ctaLabel}
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-gray-600 z-50"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Sidebar */}
          <div
            className={`fixed inset-0 bg-white z-40 flex flex-col py-4 px-8 sm:px-16 transition-transform duration-300 ease-in-out lg:hidden ${isOpen ? "translate-x-0" : "translate-x-full"
              }`}
          >
            <ul className="flex flex-col gap-6 mt-16 text-lg font-semibold overflow-scroll hide-scrollbar pb-12">
              {navLinks.map((link) => (
                <li key={link.name}>
                  {!link.dropdown ? (
                    <Link href={link.href} onClick={() => setIsOpen(false)} className={isActive(link.href) ? "text-blue-600" : "text-gray-900"}>{link.name}</Link>
                  ) : (
                    <div>
                      <button
                        onClick={() => setMobileMenuOpen(mobileMenuOpen === link.name ? null : link.name)}
                        className="flex items-center justify-between w-full"
                      >
                        <span>{link.name}</span>
                        <ChevronDown className={`transition-transform ${mobileMenuOpen === link.name ? "rotate-180" : ""}`} />
                      </button>
                      {mobileMenuOpen === link.name && (
                        <div className="mt-6 flex flex-col gap-2 text-base font-medium">
                          {platformLinks.map((item) => (
                            <DropdownItem
                              key={item.name}
                              item={item}
                              isActive={pathname === item.href}
                              onClick={() => setIsOpen(false)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
              <li>
                <a
                  href={demoHref}
                  data-analytics-id={demoAnalyticsId}
                  onClick={() => setIsOpen(false)}
                  className="text-gray-900"
                >
                  {demoLabel}
                </a>
              </li>
            </ul>

            <Link
              href={ctaHref}
              onClick={() => setIsOpen(false)}
              className="mt-auto inline-flex items-center justify-center w-full bg-[#5850EC] text-white py-4 rounded-xl font-bold"
            >
              {ctaLabel}
            </Link>
          </div>
        </nav>
      </div >
    </LayoutWrapper >
  );
};

export default Navbar;
